import React, { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { validateAccountName } from "@/lib/validators/subaccount-validators";
import type { Database } from "@/lib/supabase";

type Account = Database["public"]["Tables"]["accounts"]["Row"];

interface EditSubaccountModalProps {
  account: Account | null;
  onClose: () => void;
  onSave: (accountId: string, nome: string) => Promise<boolean>;
}

const EditSubaccountModal: React.FC<EditSubaccountModalProps> = ({
  account,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation(["agency", "common"]);
  const [nome, setNome] = useState("");
  const [validation, setValidation] = useState<{
    status: "idle" | "valid" | "invalid";
    message: string;
  }>({ status: "idle", message: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (account) {
      setNome(account.nome);
      setValidation({ status: "idle", message: "" });
      setSaving(false);
    }
  }, [account]);

  const handleChange = (value: string) => {
    setNome(value);
    setValidation({ status: "idle", message: "" });
  };

  const handleBlur = () => {
    if (!nome) return;
    const result = validateAccountName(nome.trim());
    setValidation({
      status: result.isValid ? "valid" : "invalid",
      message: result.message,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!account) return;

    const result = validateAccountName(nome.trim());
    setValidation({
      status: result.isValid ? "valid" : "invalid",
      message: result.message,
    });

    if (!result.isValid) return;

    setSaving(true);
    const saved = await onSave(account.id, nome.trim());
    setSaving(false);

    if (saved) {
      onClose();
    }
  };

  const unchanged = account ? nome.trim() === account.nome : true;

  return (
    <Dialog
      open={!!account}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="flex w-full max-w-md p-0 rounded-2xl border border-[#E5E7EB] bg-white text-[#0F172A] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] flex-col">
        <div className="p-5">
          <h3 className="text-xl font-semibold text-foreground mb-6">{t("subaccounts.editTitle")}</h3>

          <form onSubmit={handleSubmit} className="w-full gap-4 flex flex-col">
            <div className="flex flex-col w-full items-start gap-3">
              <Label htmlFor="edit_nome" className="text-sm font-medium text-foreground">
                {t("subaccounts.accountName")}
              </Label>
              <div className="relative w-full">
                <Input
                  id="edit_nome"
                  value={nome}
                  onChange={(e) => handleChange(e.target.value)}
                  onBlur={handleBlur}
                  placeholder={t("subaccounts.accountNamePlaceholder")}
                  className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] pr-10"
                  required
                  autoFocus
                  disabled={saving}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {validation.status === "valid" && (
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                  )}
                  {validation.status === "invalid" && (
                    <AlertCircle className="w-4 h-4 text-red-500" />
                  )}
                </div>
              </div>
              {validation.status === "invalid" && (
                <p className="text-red-400 text-sm">{validation.message}</p>
              )}
            </div>

            <div className="w-full flex justify-end gap-3 mt-2">
              <Button
                type="button"
                onClick={onClose}
                className="rounded-xl border border-[#E5E7EB] bg-white text-[#334155] hover:bg-[#F1F5F9] dark:border-[#3F3F46] dark:bg-transparent dark:text-[#D4D4D8] dark:hover:bg-[#3F3F46]"
                disabled={saving}
              >
                {t("common:actions.cancel")}
              </Button>
              <Button
                type="submit"
                className="rounded-xl bg-[#2563EB] text-white hover:bg-[#1D4ED8] transition-colors duration-200"
                disabled={saving || unchanged}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t("subaccounts.saving")}
                  </>
                ) : (
                  t("common:actions.save")
                )}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditSubaccountModal;
